import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  QrCode, PencilSimple, Handshake, ArrowCounterClockwise, Plus, Warning,
} from '@phosphor-icons/react';
import { bihinApi, fileUrl, STATUS_META } from './bihinApi';
import BihinQrLabelDialog from './BihinQrLabelDialog';
import { DateField } from '@/components/ui/date-time-field';
import LinkedDocsPanel from '@/pages/docs/LinkedDocsPanel';
import PurchaseSummaryCard from '@/components/PurchaseSummaryCard';
import { formatDate } from '@/utils/datetime';
import { ItemLifecycleActions } from '@/components/lifecycle/ItemLifecycleActions';
import { todayJst } from '@/utils/jstDate';

const Row = ({ label, value }) => (
  value ? (
    <div className="flex gap-2 text-xs">
      <span className="w-20 shrink-0 text-zinc-400">{label}</span>
      <span className="text-zinc-700 break-all">{value}</span>
    </div>
  ) : null
);

export default function BihinItemDetailDialog({ open, onOpenChange, itemId, canEdit, deptName = {}, onChanged, onEdit }) {
  const [item, setItem] = useState(null);
  const [loanForm, setLoanForm] = useState({ borrower_name: '', due_date: '' });
  const [stockDelta, setStockDelta] = useState('');
  const [stockReason, setStockReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [byPack, setByPack] = useState(false);

  const load = useCallback(() => {
    if (!itemId) return;
    bihinApi.item(itemId).then(setItem).catch(() => { toast.error('備品が見つかりません'); onOpenChange(false); });
  }, [itemId, onOpenChange]);
  useEffect(() => { if (open) { setItem(null); setLoanForm({ borrower_name: '', due_date: '' }); setStockDelta(''); setStockReason(''); load(); } }, [open, load]);

  const doLoan = async () => {
    setBusy(true);
    try {
      await bihinApi.loan(item.id, { borrower_name: loanForm.borrower_name || null, due_date: loanForm.due_date || null });
      toast.success('貸出を記録しました');
      load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '貸出に失敗しました');
    } finally { setBusy(false); }
  };

  const doReturn = async () => {
    setBusy(true);
    try {
      await bihinApi.returnLoan(item.active_loan.id);
      toast.success('返却しました');
      load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '返却に失敗しました');
    } finally { setBusy(false); }
  };

  // まとめ売りの品は「2セット = 60本」で入力できる。入力した単位を理由欄にも残す。
  const packQty = Number(item?.pack_qty) || 0;
  const packUnit = item?.purchase_unit || 'セット';
  const entered = Number(stockDelta) || 0;
  const baseQty = byPack && packQty > 0 ? entered * packQty : entered;
  const packPreview = byPack && packQty > 0 && entered > 0
    ? `${entered}${packUnit} = ${baseQty}${item?.unit || ''}` : null;

  // 在庫調整 = 数え直した数で置き換える（理由は必須）
  const doAdjust = async () => {
    if (!stockReason.trim()) { toast.error('在庫調整には理由が必要です'); return; }
    setBusy(true);
    try {
      const r = await bihinApi.adjustStock(item.id, {
        set_qty: baseQty, reason: stockReason.trim(), at_date: todayJst(),
      });
      toast.success(`在庫を ${r.stock_qty}${item.unit || ''} に直しました`);
      setStockDelta(''); setStockReason('');
      load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '在庫調整に失敗しました');
    } finally { setBusy(false); }
  };

  const doStock = async (sign) => {
    const d = baseQty;
    if (!d || d <= 0) { toast.error('数量を入力してください'); return; }
    setBusy(true);
    try {
      const note = packPreview ? [packPreview, stockReason].filter(Boolean).join(' / ') : stockReason;
      const r = await bihinApi.adjustStock(item.id, {
        delta: sign * d, reason: note || null, at_date: todayJst(),
      });
      toast.success(`在庫を更新しました（残 ${r.stock_qty}${item.unit || ''}）`);
      setStockDelta(''); setStockReason('');
      load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '在庫調整に失敗しました');
    } finally { setBusy(false); }
  };

  if (!open) return null;
  const st = item ? (STATUS_META[item.status] || STATUS_META.storage) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="bihin-item-detail-dialog">
        {!item ? (
          <>
            <DialogHeader><DialogTitle className="sr-only">備品詳細</DialogTitle></DialogHeader>
            <div className="py-10 text-center text-zinc-400 text-sm">読み込み中…</div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap pr-6">
                <span data-testid="bihin-detail-name" title={item.name} data-no-translate>{item.name}</span>
                {item.kind === 'equipment'
                  ? <Badge variant="outline" className={st.cls}>{st.label}</Badge>
                  : <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">消耗品</Badge>}
                {item.inspection_state === 'overdue' && (
                  <Badge variant="outline" className="bg-rose-50 text-rose-600 border-rose-200"><Warning size={10} className="mr-0.5" />点検期限切れ</Badge>
                )}
              </DialogTitle>
            </DialogHeader>

            {item.photo_url && !item.photo_is_pdf && (
              <img src={fileUrl(item.photo_url)} alt={item.name} className="w-full max-h-56 object-contain rounded-lg bg-zinc-50 border border-zinc-100" data-testid="bihin-detail-photo" />
            )}
            {item.photo_url && item.photo_is_pdf && (
              <a href={fileUrl(item.photo_url)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline" data-testid="bihin-detail-pdf-link">
                添付PDFを開く（{item.photo_filename}）
              </a>
            )}

            <div className="space-y-1.5" data-testid="bihin-detail-info">
              <ItemLifecycleActions register="bihin" itemId={item.id}
                onChanged={() => { load(); onChanged?.(); }} testId="bihin-lifecycle" />
              <Row label="品番" value={item.asset_no && <span className="font-mono">{item.asset_no}</span>} />
              <Row label="所属部門" value={deptName[item.department_id] || (item.department_id ? item.department_id : '未割当')} />
              <Row label="保管場所" value={item.location} />
              <Row label="分類" value={item.category} />
              <Row label="メーカー" value={item.maker} />
              <Row label="型式" value={item.model_no} />
              <Row label="購入日" value={item.purchase_date} />
              <Row label="価格" value={item.price != null ? `¥${Number(item.price).toLocaleString()}（1${item.unit || '個'}あたり）` : null} />
              <Row label="入数" value={item.pack_label && (
                <span data-testid="bihin-detail-pack">
                  {item.pack_label}
                  {item.pack_price != null && `・パック価格 ¥${Number(item.pack_price).toLocaleString()}`}
                  {item.unit_price != null && `・単価 ¥${Number(item.unit_price).toLocaleString()}`}
                </span>
              )} />
              <Row label="製造番号" value={item.serial_no
                ? <span className="font-mono">{item.serial_no}</span>
                : (item.serial_pending ? (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]"
                    data-testid="bihin-detail-serial-pending">製造番号 未入力</Badge>
                ) : null)} />
              <Row label="メーカー品番" value={item.maker_pn && <span className="font-mono">{item.maker_pn}</span>} />
              <Row label="仕入先" value={item.supplier_name} />
              <Row label="注文URL" value={item.reference_url && (
                <a href={item.reference_url} target="_blank" rel="noreferrer"
                  className="text-teal-700 hover:underline break-all" data-testid="bihin-detail-order-url">
                  {item.reference_url}
                </a>
              )} />
              <Row label="次回点検" value={item.next_inspection_date} />
              <Row label="メモ" value={item.notes} />
            </div>

            {/* 購入実績 — 入荷確認の実績から集計（編集権限者のみ見える） */}
            <PurchaseSummaryCard
              ledger="office" assetId={item.id}
              orderPrefill={{
                item_id: item.id, item_source: 'office',
                name: item.name, part_no: item.asset_no,
                maker: item.maker, maker_pn: item.maker_pn,
                model_no: item.model_no, uom: item.unit || '個',
                reference_url: item.reference_url,
                // 入数（1セット=30本）。発注画面で数量を在庫単位で入れるために渡す。
                pack_qty: item.pack_qty || null,
                purchase_unit: item.purchase_unit || null,
                pack_label: item.pack_label || null,
              }}
            />

            {/* 消耗品 — 在庫 */}
            {item.kind === 'consumable' && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 space-y-2" data-testid="bihin-detail-stock">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-zinc-600">在庫</span>
                  <span className={`text-lg font-bold ${item.low_stock ? 'text-rose-600' : 'text-emerald-700'}`} data-testid="bihin-detail-stock-qty">
                    {item.stock_qty ?? 0}{item.unit || ''}
                    {item.min_qty != null && <span className="text-[10px] font-normal text-zinc-400 ml-1">/ 最低 {item.min_qty}{item.unit || ''}</span>}
                  </span>
                </div>
                {canEdit && packQty > 0 && (
                  <div className="flex items-center gap-2" data-testid="bihin-stock-unit-toggle">
                    <span className="text-[11px] text-zinc-500">入力する単位</span>
                    <div className="inline-flex rounded-md border border-zinc-300 bg-white p-0.5">
                      <button type="button" onClick={() => setByPack(false)} data-testid="bihin-stock-unit-base"
                        className={`rounded px-2 py-0.5 text-[11px] ${!byPack ? 'bg-emerald-600 text-white' : 'text-zinc-600'}`}>
                        {item.unit || '個'}
                      </button>
                      <button type="button" onClick={() => setByPack(true)} data-testid="bihin-stock-unit-pack"
                        className={`rounded px-2 py-0.5 text-[11px] ${byPack ? 'bg-emerald-600 text-white' : 'text-zinc-600'}`}>
                        {packUnit}
                      </button>
                    </div>
                    <span className="text-[11px] text-zinc-400">{item.pack_label}</span>
                  </div>
                )}
                {canEdit && packPreview && (
                  <p className="text-[11px] font-semibold text-emerald-800" data-testid="bihin-stock-pack-preview">
                    {packPreview} として記録します
                  </p>
                )}
                {canEdit && (
                  <div className="flex items-center gap-1.5">
                    <Input type="number" min="0" value={stockDelta} onChange={(e) => setStockDelta(e.target.value)}
                      placeholder={byPack ? packUnit : '数量'} className="h-9 w-20" data-testid="bihin-stock-delta" />
                    <Input value={stockReason} onChange={(e) => setStockReason(e.target.value)}
                      placeholder="理由（任意）" className="h-9 flex-1" data-testid="bihin-stock-reason" />
                    <Button size="sm" variant="outline" className="h-9" disabled={busy} onClick={() => doStock(1)} data-testid="bihin-stock-in">
                      <Plus size={13} />入庫
                    </Button>
                    <Button size="sm" variant="outline" className="h-9" disabled={busy}
                      onClick={doAdjust} data-testid="bihin-stock-adjust">在庫調整</Button>
                    {/* 出庫 は状態の帯（ItemLifecycleActions）にある。二度出さない（§8.5）。 */}
                  </div>
                )}
              </div>
            )}

            {/* 備品 — 貸出/返却 */}
            {item.kind === 'equipment' && item.status !== 'disposed' && (
              <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-3 space-y-2" data-testid="bihin-detail-loan-section">
                {item.active_loan ? (
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-zinc-700">
                      <Handshake size={13} className="inline text-blue-600 mr-1" />
                      <b>{item.active_loan.borrower_name}</b> に貸出中
                      {item.active_loan.due_date && <span className="text-zinc-500 ml-1">（期限 {item.active_loan.due_date}）</span>}
                    </div>
                    <Button size="sm" className="h-9 bg-teal-700 hover:bg-teal-800" disabled={busy} onClick={doReturn} data-testid="bihin-return-btn">
                      <ArrowCounterClockwise size={13} className="mr-1" />返却する
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <div className="text-xs font-semibold text-zinc-600">貸出する</div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Input value={loanForm.borrower_name} onChange={(e) => setLoanForm((p) => ({ ...p, borrower_name: e.target.value }))}
                        placeholder="借りる人（空欄で自分）" className="h-9 flex-1 min-w-36" data-testid="bihin-loan-borrower" />
                      <DateField value={loanForm.due_date} onChange={(v) => setLoanForm((p) => ({ ...p, due_date: v }))}
                        className="w-40" inputClassName="h-9" title="返却期限" testId="bihin-loan-due" />
                      <Button size="sm" className="h-9 bg-teal-700 hover:bg-teal-800" disabled={busy} onClick={doLoan} data-testid="bihin-loan-btn">
                        <Handshake size={13} className="mr-1" />貸出
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 貸出履歴 */}
            {(item.loans || []).length > 0 && (
              <div data-testid="bihin-detail-loan-history">
                <div className="text-xs font-semibold text-zinc-600 mb-1.5">貸出履歴</div>
                <div className="space-y-1 max-h-36 overflow-y-auto">
                  {item.loans.map((l) => (
                    <div key={l.id} className="flex items-center justify-between text-[11px] rounded-lg bg-zinc-50 px-2.5 py-1.5">
                      <span className="font-semibold text-zinc-700">{l.borrower_name}</span>
                      <span className="text-zinc-500">
                        {formatDate(l.loaned_at)}
                        {' → '}
                        {l.returned_at ? formatDate(l.returned_at) : <b className="text-blue-600">貸出中</b>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <LinkedDocsPanel targetType="bihin_item" targetId={item.id} className="pt-1" />

            <BihinQrLabelDialog open={qrOpen} onOpenChange={setQrOpen}
              item={{ id: item.id, no: item.asset_no, name: item.name, sub: item.location }} />

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setQrOpen(true)} data-testid="bihin-detail-qr-btn">
                <QrCode size={14} className="mr-1" />QRラベル
              </Button>
              {canEdit && (
                <Button variant="outline" onClick={() => onEdit?.(item)} data-testid="bihin-detail-edit-btn">
                  <PencilSimple size={14} className="mr-1" />編集
                </Button>
              )}
              <Button onClick={() => onOpenChange(false)} data-testid="bihin-detail-close-btn">閉じる</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
